import { readingMinutes } from "../lib/readingTime";
import ReadingTime from "../components/ReadingTime";
import { articleLanguage, articlePath } from "../lib/articleLanguage";
import { usePublicLanguage } from "../context/PublicLanguage";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { publicApi } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import StoryCard from "@/components/StoryCard";
import CategoryTag from "@/components/CategoryTag";
import QuickBites from "@/components/public/QuickBites";
import Trending from "@/components/public/Trending";
import DailyDishBlock from "@/components/public/DailyDishBlock";
import Newsletter from "@/components/public/Newsletter";
import AdSlot from "@/components/public/AdSlot";
import { SectionHeading } from "@/components/public/SectionHeading";
import { timeAgo } from "@/lib/format";

function HeroMainDish({ article }) {
  const { t, language } = usePublicLanguage();
  const selected = articleLanguage(article, language);
  article = selected.article;
  if (!article) return null;
  return (
    <Link to={articlePath(article, selected.language)} className="group block td-fade-up" data-testid="main-dish">
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        <div className="relative overflow-hidden rounded-lg order-1 lg:order-none" style={{ aspectRatio: "16/10", background: "#111315" }}>
          <img
            src={article.hero_image}
            alt={article.hero_alt || article.headline}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
          <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full backdrop-blur" style={{ background: "rgba(8,9,10,0.7)" }}>
            <span className="td-eyebrow" style={{ color: "#FF8A1F", fontSize: "0.58rem" }}>{t('Main Dish')}</span>
          </div>
        </div>
        <div>
          <CategoryTag category={article.category} asLink={false} />
          {language === "id" && selected.language === "en" && <span className="text-xs text-[#FFB347]">English · Terjemahan belum tersedia</span>}
          <h1 lang={selected.language} className="font-display font-extrabold tracking-tight text-white mt-3 text-3xl md:text-4xl lg:text-5xl leading-[1.05] group-hover:text-[#FF8A1F] transition-colors" style={{ letterSpacing: "-0.03em" }}>
            {article.headline}
          </h1>
          <p lang={selected.language} className="mt-4 text-[#A7ABB0] leading-relaxed" style={{ fontSize: "1.1rem" }}>
            {article.excerpt}
          </p>
          <div className="mt-5 flex items-center gap-3 td-eyebrow" style={{ color: "#71767c", fontSize: "0.62rem" }}>
            <span>{timeAgo(article.published_at, language)}</span>
            {readingMinutes(article) && <><span className="opacity-40">/</span><ReadingTime article={article} /></>}
          </div>
          <span
            className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 font-display font-semibold text-[#08090A] transition-all group-hover:gap-3"
            style={{ background: "linear-gradient(135deg, #FF8A1F, #FFB347)" }}
          >{t('Read Story')}<ArrowRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function CategoryShowcase({ eyebrow, title, slug, articles }) {
  const { t } = usePublicLanguage();
  const items = articles.filter((a) => a.category === slug).slice(0, 3);
  if (items.length === 0) return null;
  return (
    <section>
      <SectionHeading
        eyebrow={t(eyebrow)}
        title={t(title)}
        action={
          <Link to={`/${slug}`} className="td-eyebrow inline-flex items-center gap-1.5 hover:gap-2.5 transition-all" style={{ color: "#FF8A1F", fontSize: "0.62rem" }}>{t('View all')}<ArrowRight size={13} />
          </Link>
        }
      />
      <div className="grid md:grid-cols-3 gap-8">
        {items.map((a) => (
          <StoryCard key={a.slug} article={a} />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = usePublicLanguage();
  useSEO({
    title: "TechDishes — Fresh Technology. Served Daily.",
    description: "The most important AI and technology stories, insights and guides — carefully researched, clearly explained, and served daily.",
    url: window.location.href,
    image: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?q=85&w=1200",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "TechDishes",
      slogan: "Fresh Technology. Served Daily.",
      url: window.location.origin,
    },
  });

  const { data: all = [] } = useQuery({ queryKey: ["articles"], queryFn: () => publicApi.articles({ limit: 60 }) });
  const { data: mainList = [] } = useQuery({ queryKey: ["main"], queryFn: () => publicApi.articles({ featured: "main" }) });
  const sidebar = useQuery({ queryKey: ["homepage-sidebar"], queryFn: publicApi.homepageSidebar });
  const { data: dailyDish } = useQuery({ queryKey: ["daily"], queryFn: publicApi.dailyDish });

  const main = mainList[0];
  const secondary = all.filter((a) => a.slug !== main?.slug).slice(0, 3);
  const latest = all.filter((a) => a.slug !== main?.slug).slice(0, 8);

  return (
    <div className="mx-auto max-w-[1320px] px-5 md:px-8 py-8 md:py-12">
      {/* Hero */}
      <section className="mb-14">
        <HeroMainDish article={main} />
      </section>

      {/* Secondary stories */}
      <section className="mb-14 grid md:grid-cols-3 gap-8 border-t pt-10" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        {secondary.map((a) => (
          <StoryCard key={a.slug} article={a} />
        ))}
      </section>

      {/* Main grid: latest + sidebar */}
      <div className="grid lg:grid-cols-3 gap-10 lg:gap-14">
        <div className="lg:col-span-2 space-y-14">
          <section>
            <SectionHeading eyebrow={t('The feed')} title={t('Latest Stories')} />
            <div className="grid sm:grid-cols-2 gap-8">
              {latest.map((a) => (
                <StoryCard key={a.slug} article={a} size="sm" showExcerpt />
              ))}
            </div>
          </section>

          <AdSlot height={120} />

          <CategoryShowcase eyebrow={t('AI Dish')} title={t('Artificial Intelligence')} slug="ai" articles={all} />
          <CategoryShowcase eyebrow={t('Robot Dish')} title={t('Robotics')} slug="robotics" articles={all} />
          <CategoryShowcase eyebrow={t('Cyber Dish')} title={t('Cybersecurity')} slug="cybersecurity" articles={all} />
          <CategoryShowcase eyebrow={t('Future Tech')} title={t('The Frontier')} slug="future-tech" articles={all} />
        </div>

        {/* Sidebar */}
        <aside className="space-y-10 lg:sticky lg:top-[84px] self-start">
          <QuickBites items={sidebar.data?.quick_bites || []} isLoading={sidebar.isLoading} isError={sidebar.isError} />
          <Trending items={sidebar.data?.highlights?.items || []} mode={sidebar.data?.highlights?.mode} isLoading={sidebar.isLoading} isError={sidebar.isError} />
          <AdSlot height={260} label={t('Advertisement')} />
        </aside>
      </div>

      {/* Daily Dish */}
      <section className="mt-16">
        <DailyDishBlock dish={dailyDish} compact />
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
